// LayerToggle — layer visibility control (US-875 · AC-3).
//
// Renders the four mockup groups (Connectivity Gaps / Infrastructure / Anchor
// Institutions / Boundaries) straight from layerConfig. Each layer is a full-width
// clickable row (square checkbox + label + description) per DESIGN.md's Layer
// Toggle Row: navy-tint hover, visible keyboard focus, color paired with text.
// Pure UI — the layers context owns state + the flicker-free map effect.
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import {
  CATEGORIES,
  GAP_STYLE_RASTER,
  GAP_STYLE_VECTOR,
  LAYERS,
} from "../../config/layerConfig";
import useLayers from "../../hooks/useLayers";

const LayerRow = ({ layer, checked, onToggle }) => (
  <Box
    component="label"
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1.25,
      px: 2,
      py: 1,
      cursor: "pointer",
      transition: "background-color 150ms ease",
      "&:hover": { bgcolor: "primary.light" },
      "&:focus-within": { bgcolor: "primary.light" },
    }}
  >
    <Checkbox
      size="small"
      checked={checked}
      onChange={onToggle}
      disableRipple
      sx={{ p: 0, mt: "1px" }}
      inputProps={{ "aria-label": layer.label }}
    />
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, lineHeight: 1.35, color: "text.primary" }}
      >
        {layer.label}
      </Typography>
      {layer.description && (
        <Typography
          variant="caption"
          sx={{ display: "block", color: "text.secondary", lineHeight: 1.35 }}
        >
          {layer.description}
        </Typography>
      )}
    </Box>
  </Box>
);

// US-920 (A-2): the gap-style switch — Continuous (raster surfaces) vs Cells
// (legacy vector tiles). Lets the stakeholder compare both renderings of the
// same toggles with one click.
const GapStyleSwitch = ({ gapStyle, setGapStyle }) => (
  <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
    <ToggleButtonGroup
      size="small"
      exclusive
      fullWidth
      value={gapStyle}
      onChange={(event, next) => next && setGapStyle(next)}
      aria-label="Gap rendering style"
    >
      <ToggleButton value={GAP_STYLE_RASTER} sx={{ textTransform: "none", py: 0.25 }}>
        Continuous
      </ToggleButton>
      <ToggleButton value={GAP_STYLE_VECTOR} sx={{ textTransform: "none", py: 0.25 }}>
        Cells
      </ToggleButton>
    </ToggleButtonGroup>
    <Typography
      variant="caption"
      sx={{ display: "block", mt: 0.5, color: "text.secondary", lineHeight: 1.3 }}
    >
      Smooth surface vs. the underlying 1 km cells — same data, two renderings.
    </Typography>
  </Box>
);

const LayerToggle = () => {
  const { visibleLayers, toggleLayer, gapStyle, setGapStyle } = useLayers();

  return (
    <Box sx={{ pb: 1 }}>
      {CATEGORIES.map((category) => {
        const layers = LAYERS.filter((l) => l.category === category.key);
        if (layers.length === 0) return null;

        return (
          <Box
            key={category.key}
            sx={{ "&:not(:last-of-type)": { borderBottom: 1, borderColor: "divider" } }}
          >
            <Typography
              component="h3"
              sx={{
                px: 2,
                pt: 2,
                pb: 0.5,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "text.secondary",
              }}
            >
              {category.label}
            </Typography>
            {category.key === "gaps" && (
              <GapStyleSwitch gapStyle={gapStyle} setGapStyle={setGapStyle} />
            )}
            {layers.map((layer) => (
              <LayerRow
                key={layer.id}
                layer={layer}
                checked={!!visibleLayers[layer.id]}
                onToggle={() => toggleLayer(layer.id)}
              />
            ))}
          </Box>
        );
      })}
    </Box>
  );
};

export default LayerToggle;
